import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import {
  ShieldAlert, ShieldCheck, ShieldHalf, Zap, Clock, RefreshCw, AlertTriangle, TrendingDown,
} from "lucide-react";

const RISK = {
  green: { icon: ShieldCheck, tint: "text-emerald-300", ring: "ring-emerald-400/40", bg: "bg-emerald-500/10", chip: "bg-emerald-950/70 border-emerald-500/40 text-emerald-200", label: "Low risk" },
  amber: { icon: ShieldHalf,  tint: "text-amber-300",   ring: "ring-amber-400/40",   bg: "bg-amber-500/10",   chip: "bg-amber-950/70 border-amber-500/40 text-amber-200",   label: "Watch" },
  red:   { icon: ShieldAlert, tint: "text-rose-300",    ring: "ring-rose-400/40",    bg: "bg-rose-500/10",    chip: "bg-rose-950/70 border-rose-500/40 text-rose-200",       label: "High risk" },
};

function fmt(iso) {
  const d = new Date(iso);
  return d.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function countdown(iso) {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return "starting";
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  const d = Math.floor(h / 24);
  return d > 0 ? `in ${d}d ${h % 24}h` : `in ${h}h ${m}m`;
}

export default function AlertnessMonitor() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    http.get("/alertness").then((r) => setData(r.data)).catch(() => setData({ shifts: [] })).finally(() => setLoading(false));
  };
  useEffect(load, []);

  if (loading && !data) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64 bg-white/10" />
        <div className="grid sm:grid-cols-2 gap-4">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-40 rounded-2xl bg-white/5" />)}
        </div>
      </div>
    );
  }

  const shifts = data?.shifts || [];
  const highest = data?.highest_risk;

  return (
    <section className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
            <ShieldAlert className="w-6 h-6 text-rose-300" /> Safety Alertness
          </h2>
          <p className="text-slate-400 text-sm mt-1">
            Predicted alertness for each upcoming shift — especially critical for pilots, train drivers, and long overnight roles.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={load}
          data-testid="btn-refresh-alertness"
          className="rounded-full bg-transparent border-white/15 text-slate-200 hover:bg-white/5"
        >
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {/* summary strip */}
      <div className="grid grid-cols-3 gap-4">
        <SummaryTile icon={TrendingDown} tint="text-amber-300" label="Sleep debt" value={`${data?.sleep_debt ?? 0}h`} />
        <SummaryTile icon={Clock} tint="text-indigo-300" label="Avg sleep" value={`${data?.avg_hours ?? 0}h`} />
        <SummaryTile icon={Zap} tint="text-rose-300" label="Shifts flagged" value={shifts.filter((s) => s.alertness.risk !== "green").length} />
      </div>

      {/* highest risk callout */}
      {highest && (
        <div className={`circa-card p-6 ring-1 ${RISK[highest.alertness.risk].ring}`}>
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-2xl ${RISK[highest.alertness.risk].bg} flex items-center justify-center shrink-0`}>
              <AlertTriangle className={`w-6 h-6 ${RISK[highest.alertness.risk].tint}`} />
            </div>
            <div className="flex-1">
              <div className="pill mb-1 border-white/10 bg-slate-900/60 text-slate-300">Priority</div>
              <div className="font-display text-lg font-semibold">{highest.title}</div>
              <div className="text-xs text-slate-400 font-mono-ui">{fmt(highest.start)} · {countdown(highest.start)}</div>
              <div className={`mt-2 text-sm ${RISK[highest.alertness.risk].tint}`}>{highest.alertness.advice}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest font-mono-ui text-slate-500">Score</div>
              <div className={`font-display text-3xl font-extrabold ${RISK[highest.alertness.risk].tint}`}>{highest.alertness.score}</div>
            </div>
          </div>
        </div>
      )}

      {/* All upcoming shifts */}
      {shifts.length === 0 ? (
        <div className="circa-card p-8 text-center text-slate-500 text-sm">
          No upcoming shifts in the next 7 days. Add shifts in your calendar to see alertness predictions.
        </div>
      ) : (
        <div className="grid gap-4">
          {shifts.map((s) => <AlertnessCard key={s.id} shift={s} />)}
        </div>
      )}
    </section>
  );
}

function AlertnessCard({ shift }) {
  const r = RISK[shift.alertness.risk] || RISK.green;
  const Icon = r.icon;
  return (
    <div className="circa-card p-5" data-testid={`alertness-card-${shift.id}`}>
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 rounded-2xl ${r.bg} flex items-center justify-center shrink-0`}>
          <Icon className={`w-6 h-6 ${r.tint}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display text-base font-semibold">{shift.title}</span>
            <span className={`pill text-[9px] ${r.chip}`}>{r.label}</span>
            {shift.role && <span className="pill text-[9px] bg-slate-900/60 border-white/10 text-slate-300">{shift.role}</span>}
          </div>
          <div className="text-xs text-slate-400 font-mono-ui mt-1">
            {fmt(shift.start)} → {fmt(shift.end)} · {countdown(shift.start)}
          </div>
          <div className={`text-sm ${r.tint} mt-2 leading-relaxed`}>{shift.alertness.advice}</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest font-mono-ui text-slate-500">Score</div>
          <div className={`font-display text-2xl font-extrabold ${r.tint}`}>{shift.alertness.score}</div>
        </div>
      </div>
      {shift.alertness.factors?.length > 0 && (
        <div className="mt-4 pl-16 flex flex-wrap gap-2">
          {shift.alertness.factors.map((f, i) => (
            <span key={i} className="pill bg-slate-900/60 border-white/10 text-slate-400 text-[10px] normal-case tracking-normal font-normal font-sans">
              {f.label}
              <span className="text-rose-400 ml-1 font-mono-ui">{f.delta}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function SummaryTile({ icon: Icon, label, value, tint }) {
  return (
    <div className="circa-card p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs uppercase tracking-widest font-mono-ui text-slate-500">{label}</span>
        <Icon className={`w-4 h-4 ${tint}`} />
      </div>
      <div className={`font-display text-2xl font-bold ${tint}`}>{value}</div>
    </div>
  );
}
